<script setup>
import { computed } from 'vue';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { ARTICLE_STATUSES } from 'dashboard/helper/portalHelper';

import { Badge } from 'next/polaris/ui/badge';
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from 'next/polaris/ui/item';
import MessageFormatter from 'shared/helpers/MessageFormatter';

const props = defineProps({
  id: { type: [String, Number], default: 0 },
  title: { type: String, default: '' },
  description: { type: String, default: '' },
  category: { type: String, default: '' },
  locale: { type: String, default: '' },
  content: { type: String, default: '' },
  portalSlug: { type: String, required: true },
  accountId: { type: [String, Number], default: 0 },
  status: { type: String, default: '' },
  updatedAt: { type: Number, default: 0 },
});

const MAX_LENGTH = 300;

const navigateTo = computed(() => {
  return frontendURL(
    `accounts/${props.accountId}/portals/${props.portalSlug}/${props.locale}/articles/edit/${props.id}`
  );
});

const updatedAtTime = computed(() => {
  if (!props.updatedAt) return '';
  return dynamicTime(props.updatedAt);
});

const truncatedContent = computed(() => {
  if (!props.content) return props.description || '';

  // Use MessageFormatter to properly convert markdown to plain text
  const formatter = new MessageFormatter(props.content);
  const plainText = formatter.plainText.trim();

  return plainText.length > MAX_LENGTH
    ? `${plainText.substring(0, MAX_LENGTH)}...`
    : plainText;
});

const statusVariant = computed(() => {
  switch (props.status) {
    case ARTICLE_STATUSES.ARCHIVED:
      return 'outline';
    case ARTICLE_STATUSES.DRAFT:
      return 'warning';
    default:
      return 'success';
  }
});
</script>

<template>
  <Item
    as-child
    size="sm"
    class="w-full cursor-pointer flex-nowrap items-start px-3 text-start text-polaris-foreground hover:bg-polaris-accent"
  >
    <!-- Article row in the shape of the kit's article card: title, status Badge (in words), category, snippet, time. -->
    <router-link :to="navigateTo">
      <ItemContent class="min-w-0 gap-0.5">
        <ItemTitle class="w-full justify-between font-normal">
          <span class="flex min-w-0 items-center gap-2">
            <span class="truncate font-medium" :title="title">{{ title }}</span>
            <Badge v-if="status" :variant="statusVariant" class="capitalize">
              {{ status }}
            </Badge>
          </span>
          <span
            v-if="updatedAtTime"
            class="shrink-0 text-xs font-normal text-polaris-muted-foreground"
          >
            {{ updatedAtTime }}
          </span>
        </ItemTitle>
        <p
          v-if="category"
          class="mb-0 truncate type-polaris-small-label capitalize text-polaris-muted-foreground"
          :title="category"
        >
          {{ category }}
        </p>
        <ItemDescription
          v-if="truncatedContent"
          class="mb-0 line-clamp-2 break-words text-xs"
        >
          {{ truncatedContent }}
        </ItemDescription>
      </ItemContent>
    </router-link>
  </Item>
</template>
